import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  getParentMixtureIdFromTable,
  INVALID_PARENT_MIXTURE_ID,
} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {MixtureNoteLinkedGroupAdapter} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {
  InsertInstructionsMixtureQuery,
  InsertInstructionsMixtureQueryVariables,
} from 'notebook/notes/groups/action-functions/__generated/InsertInstructionsMixtureQuery';
import {InsertInstructionsParentMixtureQuery} from 'notebook/notes/groups/action-functions/__generated/InsertInstructionsParentMixtureQuery';
import {GroupActionFunctionParameters} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {prepareNotePartForStore} from 'notebook/notes/NoteStoreTransforms';
import {RETRIEVE_PRIMARY_SAMPLES_INTERVAL_MS} from 'notebook/structured-tables/StructuredTableConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';

type MixtureInstructionsGQL = NonNullable<
  ExtractByTypename<InsertInstructionsMixtureQuery['mixture'], 'MixtureNode'>['instructions']
>;

type MixtureWithParentInstructionsOrInaccessible = InsertInstructionsParentMixtureQuery['mixture'];

const BUTTON_TITLE = 'Insert parent instructions into entry';
const DISABLED_MESSAGE_NO_PARENT = 'No parent mixture is set';
const DISABLED_MESSAGE_INACCESSIBLE =
  'You do not have sufficient permissions to access the parent instructions for this mixture table';
const DISABLED_MESSAGE_OUT_OF_DATE_PARENT =
  'The parent mixture value is out-of-date. Please refresh the table in order to insert instructions';
const DISABLED_MESSAGE_NO_INSTRUCTIONS = 'Parent mixture does not have instructions';

export const INSERT_INSTRUCTIONS_PARENT_MIXTURE_QUERY: DocumentNode = gql`
  query InsertInstructionsParentMixtureQuery($mixtureId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        id
        parentMixture {
          ... on MixtureNode {
            id
            instructions {
              id
              noteJson
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const INSERT_INSTRUCTIONS_MIXTURE_QUERY: DocumentNode = gql`
  query InsertInstructionsMixtureQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        id
        instructions {
          id
          noteJson
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface PresentationalProps {
  isLoading?: boolean;
  instructions: MixtureInstructionsGQL | null;
  disabledMessage?: string;
  insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => void;
  refetchInstructions?: () => void;
  locked: boolean;
}

export function InsertParentMixtureInstructionsButton(props: PresentationalProps): JSX.Element {
  const {isLoading, instructions, disabledMessage, insertAfterGroup, refetchInstructions, locked} = props;

  useEffect(() => {
    if (refetchInstructions == null) {
      return;
    }
    // We want this to re-fetch with the same frequency as useLiveData
    const intervalId = setInterval(refetchInstructions, RETRIEVE_PRIMARY_SAMPLES_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [refetchInstructions]);

  const insertInstructionsIntoEntry = (instructions: MixtureInstructionsGQL): void => {
    // Clone query data before transformation so addCid(...) never mutates Apollo-cached objects.
    const notePartsToInsert = deepCopy(instructions.noteJson).noteParts;
    const cidIdMap = {};
    for (const notePart of notePartsToInsert) {
      prepareNotePartForStore(notePart, cidIdMap);
    }
    insertAfterGroup(notePartsToInsert as unknown as Array<NotePart>, cidIdMap);
    analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.INSERTED_PARENT_MIXTURE_INSTRUCTIONS_INTO_ENTRY);
  };

  const isDisabled = locked || instructions == null || instructions.noteJson.noteParts.length === 0;
  let tooltipTitle;
  if (!isDisabled || isLoading || locked) {
    tooltipTitle = BUTTON_TITLE;
  } else {
    tooltipTitle = disabledMessage || DISABLED_MESSAGE_NO_INSTRUCTIONS;
  }
  return (
    <Tooltip title={tooltipTitle}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        className="u-mini-spacing-left"
        disabled={isDisabled}
        icon="insert-instructions"
        onClick={() => {
          insertInstructionsIntoEntry(instructions!);
        }}
      />
    </Tooltip>
  );
}

function QueryingInsertParentMixtureInstructionsButtonForUnsubmittedGroup(
  props: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras> & {
    parentMixtureId: string;
  }
): JSX.Element {
  return (
    <BenchlingQuery<InsertInstructionsMixtureQuery, InsertInstructionsMixtureQueryVariables>
      query={INSERT_INSTRUCTIONS_MIXTURE_QUERY}
      variables={{mixtureId: props.parentMixtureId}}
      renderLoading={() => (
        <InsertParentMixtureInstructionsButton
          insertAfterGroup={props.insertAfterGroup}
          instructions={null}
          isLoading={true}
          locked={props.locked}
        />
      )}
      fetchPolicy="network-only"
      renderCachedDataWhenRefetching={true}
    >
      {(data, {refetch}) => {
        let instructions = null;
        let disabledMessage;
        if (isInaccessible(data.mixture)) {
          disabledMessage = DISABLED_MESSAGE_INACCESSIBLE;
        } else {
          instructions = data.mixture.instructions;
        }
        return (
          <InsertParentMixtureInstructionsButton
            insertAfterGroup={props.insertAfterGroup}
            instructions={instructions}
            disabledMessage={disabledMessage}
            refetchInstructions={refetch}
            locked={props.locked}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface SubmittedGroupProps extends Pick<
  GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>,
  'locked' | 'insertAfterGroup'
> {
  parentMixtureId: string;
  mixtureWithParentInstructions: MixtureWithParentInstructionsOrInaccessible;
  refetchInstructions: () => void;
}

function InsertParentMixtureInstructionsButtonForSubmittedGroup(props: SubmittedGroupProps): JSX.Element {
  const {mixtureWithParentInstructions, parentMixtureId, refetchInstructions} = props;

  useOnChangeOnly(() => {
    refetchInstructions();
  }, [parentMixtureId]);

  let instructions = null;
  let disabledMessage;
  if (isInaccessible(mixtureWithParentInstructions)) {
    disabledMessage = DISABLED_MESSAGE_INACCESSIBLE;
  } else {
    const liveParentMixtureOrInaccessible = mixtureWithParentInstructions.parentMixture;
    if (liveParentMixtureOrInaccessible != null && isInaccessible(liveParentMixtureOrInaccessible)) {
      disabledMessage = DISABLED_MESSAGE_INACCESSIBLE;
    } else if (
      liveParentMixtureOrInaccessible == null ||
      liveParentMixtureOrInaccessible.id !== parentMixtureId
    ) {
      disabledMessage = DISABLED_MESSAGE_OUT_OF_DATE_PARENT;
    } else {
      instructions = liveParentMixtureOrInaccessible.instructions;
    }
  }
  return (
    <InsertParentMixtureInstructionsButton
      {...props}
      instructions={instructions}
      disabledMessage={disabledMessage}
    />
  );
}

function QueryingInsertParentMixtureInstructionsButtonForSubmittedGroup(
  props: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras> & {
    mixtureId: string;
    parentMixtureId: string;
  }
): JSX.Element {
  return (
    <BenchlingQuery<InsertInstructionsParentMixtureQuery, InsertInstructionsMixtureQueryVariables>
      query={INSERT_INSTRUCTIONS_PARENT_MIXTURE_QUERY}
      variables={{mixtureId: props.mixtureId}}
      renderLoading={() => (
        <InsertParentMixtureInstructionsButton
          insertAfterGroup={props.insertAfterGroup}
          instructions={null}
          isLoading={true}
          locked={props.locked}
        />
      )}
      fetchPolicy="network-only"
      renderCachedDataWhenRefetching={true}
    >
      {(data, {refetch}) => (
        <InsertParentMixtureInstructionsButtonForSubmittedGroup
          mixtureWithParentInstructions={data.mixture}
          parentMixtureId={props.parentMixtureId}
          insertAfterGroup={props.insertAfterGroup}
          locked={props.locked}
          refetchInstructions={refetch}
        />
      )}
    </BenchlingQuery>
  );
}

export default function QueryingInsertParentMixtureInstructionsButton(
  props: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): JSX.Element {
  const mixtureGroupAdapter = props.noteLinkedGroupAdapter as MixtureNoteLinkedGroupAdapter;
  const parentMixtureIdInTable = getParentMixtureIdFromTable(
    mixtureGroupAdapter.getMixtureMetadataStructuredTable(props.noteLinkedGroup)
  );
  // Treat the "invalid input" sentinel (returned only when ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is
  // on) the same as a cleared cell: show the disabled "no parent" UI rather than attempting to
  // query a non-existent mixture by the sentinel value.
  if (parentMixtureIdInTable == null || parentMixtureIdInTable === INVALID_PARENT_MIXTURE_ID) {
    return (
      <InsertParentMixtureInstructionsButton
        instructions={null}
        insertAfterGroup={props.insertAfterGroup}
        disabledMessage={DISABLED_MESSAGE_NO_PARENT}
        locked={props.locked}
      />
    );
  }
  const mixtureId = mixtureGroupAdapter.getMixtureId(props.noteLinkedGroup);
  if (mixtureId != null) {
    return (
      <QueryingInsertParentMixtureInstructionsButtonForSubmittedGroup
        mixtureId={mixtureId}
        parentMixtureId={parentMixtureIdInTable}
        {...props}
      />
    );
  } else {
    return (
      <QueryingInsertParentMixtureInstructionsButtonForUnsubmittedGroup
        parentMixtureId={parentMixtureIdInTable}
        {...props}
      />
    );
  }
}
